/**
 * Tour Service
 *
 * Handles First Time Wizard tour status API operations.
 *
 * API Endpoints:
 * - GET /tour-status/{tourKey} - Get tour status for current user
 * - POST /tour-status/{tourKey} - Update tour status (upsert)
 * - GET /tour-status - List all tour statuses (for future Tour Library)
 *
 * Key Behaviors:
 * - GET never returns 404 - returns default "never viewed" response for unknown tours
 * - POST creates record on first call, updates on subsequent calls
 * - All endpoints are scoped to current user/org via cookie auth
 */

import { apiService, ApiResponse } from './apiService'
import type { TourStatus, TourStatusUpdate } from '../types/tour.types'

/**
 * Tour Service
 *
 * Manages tour status persistence with the backend API.
 * Uses cookie-based authentication automatically via apiService.
 */
class TourService {
  private readonly TOUR_STATUS_ENDPOINT = '/tour-status'

  /**
   * Get tour status for a specific tour
   *
   * Returns the current user's tour status within their current org.
   * Never returns 404 - returns default "never viewed" response for tours
   * that haven't been viewed yet.
   *
   * @param tourKey - Unique tour identifier (e.g., "workflow-data")
   * @returns Tour status (always succeeds unless network/auth error)
   *
   * @example
   * const result = await tourService.getStatus('workflow-data')
   * if (result.success && result.data) {
   *   if (result.data.dismissed) {
   *     // Tour was previously dismissed, don't show
   *   } else {
   *     // Show tour
   *   }
   * }
   */
  async getStatus(tourKey: string): Promise<ApiResponse<TourStatus>> {
    return apiService.get<TourStatus>(
      `${this.TOUR_STATUS_ENDPOINT}/${encodeURIComponent(tourKey)}`,
      { suppressErrors: true } // Fail silently - we'll show tour anyway on error
    )
  }

  /**
   * Update tour status (upsert)
   *
   * Creates a new record if this is the first view, or updates existing.
   * Tracks step progress and handles dismissal.
   *
   * @param tourKey - Unique tour identifier
   * @param data - Update payload with step info and optional dismiss flag
   * @returns Updated tour status
   *
   * @example
   * // Record viewing step 3 of 5
   * await tourService.updateStatus('workflow-data', {
   *   maxStepReached: 3,
   *   totalSteps: 5
   * })
   *
   * @example
   * // Dismiss the tour
   * await tourService.updateStatus('workflow-data', {
   *   maxStepReached: 5,
   *   totalSteps: 5,
   *   dismissed: true
   * })
   */
  async updateStatus(
    tourKey: string,
    data: TourStatusUpdate
  ): Promise<ApiResponse<TourStatus>> {
    return apiService.post<TourStatus>(
      `${this.TOUR_STATUS_ENDPOINT}/${encodeURIComponent(tourKey)}`,
      data
    )
  }

  /**
   * List all tour statuses for current user
   *
   * Returns all tour statuses for the current user in their current org.
   * Used for the Tour Library feature (future).
   *
   * @returns Array of tour statuses
   *
   * @example
   * const result = await tourService.listAll()
   * if (result.success && result.data) {
   *   const completedTours = result.data.filter(t => t.dismissed)
   *   console.log(`Completed ${completedTours.length} tours`)
   * }
   */
  async listAll(): Promise<ApiResponse<TourStatus[]>> {
    return apiService.get<TourStatus[]>(this.TOUR_STATUS_ENDPOINT)
  }
}

// Export singleton instance
export const tourService = new TourService()
export default tourService
